import React from 'react';
import moment from 'moment';
import DayPicker from 'react-day-picker';
import { defaultTemplate } from 'core/hoc';
import { BaasicInput, BaasicDropdown, Loader, SliderCheckBox, DatePicker, TimePicker } from 'core/components';
import { BaasicDraggableContainer } from './BaasicDraggableContainer';
import { observer } from 'mobx-react';
import { renderIf } from 'core/utils';
import _ from 'lodash';
import { ThreeStateToggle } from 'common/utils';

const ReportEditTemplate = ({
	t,
	reportEditViewStore,
	reportEditViewStore: {
		form,
		loading,
		handleBackButtonClick,
		userDropdownStore,
		organizationDropdownStore,
		reportFrequencyDropdownStore,
		reportCategoryDropdownStore,
		reportTemplateDropdownStore,
		isTemplate,
		isEdit,
		isSuperAdmin,
		onSaveAndSendReport,
		datePickerFormat,
	},
}) => {
	if (loading) return <Loader />;
	return (
		<div>
			<div className="layout__main__subheader">
				<div className="flex--primary">
					<button className="btn layout__main__subheader__back" onClick={handleBackButtonClick}>
						<i className="fa fa-long-arrow-alt-left spc--right--tny"></i>
						{t('BACK')}
					</button>
					<h3 className="layout__main__subheader__title">
						{isTemplate ? t('REPORTS.TEMPLATEDETAILS') : t('REPORTS.REPORTDETAILS')}
					</h3>
				</div>
			</div>
			<div className="container container--lrg">
				<div className="container__group__wrapper">
					<form onSubmit={form.onSubmit}>
						{isSuperAdmin && (
							<div className="row">
								<div className="col col-sml-12 col-med-6 spc--bottom--sml">
									<BaasicDropdown
										dropdownStore={organizationDropdownStore}
										placeholder={t('REPORTS.PLACEHOLDER.CHOOSEORGANIZATION')}
										label={t('REPORTS.ORGANIZATION')}
										isDisabled={isEdit}
									/>
								</div>
							</div>
						)}
						{(!isSuperAdmin || (isSuperAdmin && organizationDropdownStore.value)) && (
							<div>
								<div className="row">
									<div className="col col-sml-12 col-med-6">
										<BaasicInput field={form.$('name')} autoFocus />
									</div>
									<div className="col col-sml-12 col-med-6 spc--bottom--sml">
										<SliderCheckBox field={form.$('isActive')} />
									</div>
									{!isTemplate && (
										<div className="col col-sml-12 col-med-6 spc--bottom--sml">
											<BaasicDropdown
												dropdownStore={reportTemplateDropdownStore}
												placeholder={t('REPORTS.PLACEHOLDER.CHOOSETEMPLATE')}
												isClearable={true}
												isDisabled={isEdit}
											/>
										</div>
									)}
								</div>
								<div className="row">
									<div className="col col-sml-12 col-med-12 spc--bottom--sml">
										<BaasicDropdown
											dropdownStore={userDropdownStore}
											placeholder={t('REPORTS.PLACEHOLDER.CHOOSEUSER')}
										/>
									</div>
								</div>
								<div className="row">
									<div className="col col-sml-12 col-med-12 spc--bottom--sml">
										<DatePicker field={form.$('scheduledDate')} format={datePickerFormat} />
									</div>
									<div className="col col-sml-12 col-med-12 spc--bottom--sml">
										<TimePicker field={form.$('sendAt')} />
									</div>
								</div>
								<div className="row">
									<div className="col col-sml-12 col-med-6 spc--bottom--sml">
										<BaasicDropdown dropdownStore={reportCategoryDropdownStore} />
									</div>
									<div className="col col-sml-12 col-med-6 spc--bottom--sml">
										<BaasicDropdown
											dropdownStore={reportFrequencyDropdownStore}
											placeholder={t('REPORTS.PLACEHOLDER.CHOOSEFREQUENCY')}
										/>
									</div>
								</div>
								{form.$('reportCategoryId').value && (
									<div className="draggable__table__wrap">
										<div className="table__settings draggable__table">
											<div className="table__settings__box">
												<div className="table__settings__scroll">
													<BaasicDraggableContainer t={t} reportEditViewStore={reportEditViewStore} />
												</div>
											</div>
											<ColumnValues t={t} reportEditViewStore={reportEditViewStore} />
										</div>
									</div>
								)}
								<hr className="separator--primary spc--bottom--sml" />

								{form.$('reportCategoryId').value && (
									<div className="flex--secondary pos--rel">
										<div>
											<button
												className="btn btn--med btn--primary spc--right--sml spc--bottom--sml"
												type="submit"
												disabled={loading}
											>
												{loading ? (
													<div className="spinnercont spc--right--sml">
														<div className="spinner"></div>
													</div>
												) : null}
												{t('SAVE')}
											</button>
											<button
												type="button"
												className="btn btn--med btn--outline spc--right--sml spc--bottom--sml"
												onClick={handleBackButtonClick}
											>
												{t('CANCEL')}
											</button>
										</div>
										{!isTemplate && (
											<React.Fragment>
												<button
													type="button"
													className="btn btn--med btn--primary spc--bottom--sml"
													disabled={loading}
													onClick={() => onSaveAndSendReport()}
												>
													{loading ? (
														<div className="spinnercont spc--right--sml">
															<div className="spinner"></div>
														</div>
													) : null}
													{t('REPORTS.SENDNOW')}
												</button>
											</React.Fragment>
										)}
									</div>
								)}
							</div>
						)}
					</form>
				</div>
			</div>
		</div>
	);
};

export default defaultTemplate(ReportEditTemplate);

const ColumnValues = observer(({ t, reportEditViewStore, reportEditViewStore: { selectedColumn } }) => {
	return (
		<div className="table__settings__box">
			<div className={`table__settings__scroll`}>
				<div className="table__settings__item first">{selectedColumn.isFilterable ? t(selectedColumn.title) : ''}</div>
				<div className="table__settings__filter">
					{(() => {
						switch (selectedColumn.filterType) {
							case 1:
								return <SearchFilter t={t} reportEditViewStore={reportEditViewStore} />;
							case 2:
								return <DateTimeFilter t={t} reportEditViewStore={reportEditViewStore} />;
							case 3:
								return (
									<ThreeStateToggle
										t={t}
										threeState={selectedColumn}
										first={{ value: true, translation: 'REPORTS.FILTER.YES' }}
										second={{ value: null, translation: 'REPORTS.FILTER.BOTH' }}
										third={{ value: false, translation: 'REPORTS.FILTER.NO' }}
									/>
								);
							case 4:
								return <NumericFilter t={t} reportEditViewStore={reportEditViewStore} />;
							default:
								return (
									<div className="illustrate__wrapper">
										<div className="w--250--px illustrate--center">
											<img
												className="display--b spc--bottom--med spc--top--med"
												src={require('themes/img/es-filter.svg')}
											/>
											<p className="spc--bottom--med w--250--px align--h--center type--center">
												{t('REPORTS.NOTFILTERABLE')}
											</p>
										</div>
									</div>
								);
						}
					})()}
				</div>
			</div>
		</div>
	);
});

const SearchFilter = observer(
	({
		t,
		reportEditViewStore: {
			filterForm,
			handleSearchColumnItemsDebounced,
			onSelectColumnItemCheckbox,
			selectedColumn,
			selectedColumn: { allItemValues },
			isDataLoading,
			onSearchContainsFilterChange,
			onColumnFilterScroll,
			scrollContainerRef,
		},
	}) => {
		return (
			<React.Fragment>
				{selectedColumn.isSearchable && (
					<React.Fragment>
						{renderIf(!filterForm.$('search').value)(
							<BaasicInput
								field={filterForm.$('contains')}
								placeholder={t(filterForm.$('contains').placeholder)}
								onKeyUp={() => onSearchContainsFilterChange(filterForm.$('contains').value)}
								onKeyDown={handleKeyDown}
								onPaste={e => onSearchContainsFilterChange(e.clipboardData.getData('text'))}
							/>
						)}
						{renderIf(!filterForm.$('contains').value)(
							<div className="form__wrap">
								<BaasicInput
									autoComplete={'off'}
									field={filterForm.$('search')}
									onKeyUp={() => handleSearchColumnItemsDebounced(selectedColumn, filterForm.$('search').value)}
									onKeyDown={handleKeyDown}
									onPaste={e => handleSearchColumnItemsDebounced(selectedColumn, e.clipboardData.getData('text'))}
								/>
							</div>
						)}
					</React.Fragment>
				)}

				{renderIf(!filterForm.$('contains').value)(
					<div ref={scrollContainerRef} className="table__settings__filter__wrap" onScroll={onColumnFilterScroll}>
						{isDataLoading ? (
							<div className="spinnercont spinnercont--g is-local">
								<div className="spinner spinner--g"></div>
							</div>
						) : (
							_.map(allItemValues, (item, idx) => {
								return (
									<div key={idx} className="table__settings__item">
										<input
											id={idx}
											className="input--check"
											type="checkbox"
											checked={item.isSelected}
											onChange={() => onSelectColumnItemCheckbox(selectedColumn, item)}
										/>
										<label htmlFor={idx}>{item.name}</label>
									</div>
								);
							})
						)}
					</div>
				)}
			</React.Fragment>
		);
	}
);

const DateTimeFilter = observer(
	({
		t,
		reportEditViewStore: { onSpecificDayClick, selectedColumn, onDateTimeOptionChange, onUseSpecificDateChange },
	}) => {
		const { useSpecificDate, specificDate } = selectedColumn;

		return (
			<div className="table--settings__filter">
				<div className="spc--bottom--sml spc--top--tny input--toggle__wrap">
					<div>
						<input
							id="useSpecificDate"
							className="input--toggle input--toggle--secondary"
							type="checkbox"
							checked={useSpecificDate}
							onChange={e => onUseSpecificDateChange(e.currentTarget.checked)}
						/>
						<label htmlFor="useSpecificDate"></label>
					</div>
					<span className="spc--left--sml" htmlFor="useSpecificDate">
						{t('REPORTS.FILTER.USESPECIFICDATE')}
					</span>
				</div>
				<div className="separator--primary spc--bottom--med"></div>
				{!useSpecificDate &&
					_.map(selectedColumn.dateTimeOptions, (option, idx) => (
						<div className="spc--bottom--tny" key={idx}>
							<input
								type="radio"
								className="input--radio"
								id={idx}
								value={option.value}
								checked={option.checked}
								onChange={() => onDateTimeOptionChange(option)}
							/>
							<label htmlFor={idx}>{option.title}</label>
						</div>
					))}
				{useSpecificDate && (
					<div className="table__settings__filter__calendar">
						<DayPicker
							value={new Date(specificDate)}
							selectedDays={specificDate ? moment(specificDate).toDate() : moment().toDate()}
							month={new Date()}
							onDayClick={onSpecificDayClick}
						></DayPicker>
					</div>
				)}
			</div>
		);
	}
);

const NumericFilter = observer(
	({
		t,
		reportEditViewStore: {
			filterForm,
			onNumericSpecificFilterChange,
			onNumericMinFilterChange,
			onNumericMaxFilterChange,
		},
	}) => {
		return (
			<div>
				{renderIf(!filterForm.$('numberRangeStart').value && !filterForm.$('numberRangeEnd').value)(
					<BaasicInput
						field={filterForm.$('specificNumber')}
						onKeyUp={() => onNumericSpecificFilterChange(filterForm.$('specificNumber').value)}
						onKeyDown={handleKeyDown}
						onPaste={e => onNumericSpecificFilterChange(e.clipboardData.getData('text'))}
					/>
				)}
				{renderIf(
					!filterForm.$('numberRangeStart').value &&
						!filterForm.$('numberRangeEnd').value &&
						!filterForm.$('specificNumber').value
				)(<div className="spc--bottom--sml">{t('FILTER.NUMBEROR')}</div>)}
				{renderIf(!filterForm.$('specificNumber').value)(
					<section>
						<BaasicInput
							field={filterForm.$('numberRangeStart')}
							onKeyUp={() => onNumericMinFilterChange(filterForm.$('numberRangeStart').value)}
							onKeyDown={handleKeyDown}
							onPaste={e => onNumericMinFilterChange(e.clipboardData.getData('text'))}
						/>
						<BaasicInput
							field={filterForm.$('numberRangeEnd')}
							onKeyUp={() => onNumericMaxFilterChange(filterForm.$('numberRangeEnd').value)}
							onKeyDown={handleKeyDown}
							onPaste={e => onNumericMaxFilterChange(e.clipboardData.getData('text'))}
						/>
					</section>
				)}
			</div>
		);
	}
);

const handleKeyDown = e => {
	// If the Enter (13) is pressed
	if (e.keyCode === 13) {
		e.preventDefault(); // Prevent the default behavior (form submission)
	}
};



// WEBPACK FOOTER //
// ./src/themes/default/modules/report/pages/ReportEditTemplate.jsx